import { Download, Mail } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Document, Page, PDFDownloadLink, StyleSheet, Text, View } from "@react-pdf/renderer";
import GenericModal from "components/GenericModal";
import { useMemo, useState } from "react";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "Helvetica",
    fontSize: 12,
  },
  invoiceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 32,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 16,
  },
  invoiceInfo: {
    marginBottom: 4,
  },
  companyInfo: {
    textAlign: "right",
    marginTop: 4,
  },
  sectionTitle: {
    fontSize: 12,
    color: "#666",
    marginBottom: 4,
  },
  customerInfo: {
    marginBottom: 32,
  },
  customerName: {
    fontWeight: "bold",
    marginBottom: 4,
  },
  table: {
    marginBottom: 32,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f5f5f5",
    padding: 8,
    borderBottom: "1px solid #e0e0e0",
  },
  tableHeaderCell: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#666",
    textTransform: "uppercase",
    paddingLeft: 4,
    paddingRight: 4,
  },
  tableRow: {
    flexDirection: "row",
    padding: 8,
    borderBottom: "1px solid #e0e0e0",
  },
  tableCell: {
    fontSize: 10,
    paddingLeft: 4,
    paddingRight: 4,
  },
  tableCellMedium: {
    fontSize: 10,
    fontWeight: "bold",
    paddingLeft: 4,
    paddingRight: 4,
  },
  voidLabel: {
    color: "#F44335",
    display: "inline-block",
  },
  // Column widths for detail view
  detailCol1: { width: "10%" }, // DATE
  detailCol2: { width: "15%" }, // EVENT/JOB
  detailCol2Long: { width: "20%" }, // EVENT/JOB
  detailCol3: { width: "15%" }, // EMPLOYEE NAME
  detailCol3Long: { width: "20%" }, // EMPLOYEE NAME
  detailCol4: { width: "12%" }, // POSITION
  detailCol5: { width: "10%" }, // PAYCODE
  detailCol6: { width: "10%" }, // REG HOURS
  detailCol7: { width: "10%" }, // OT HOURS
  detailCol8: { width: "8%" }, // RATE
  detailCol9: { width: "10%" }, // AMOUNT

  // Column widths for summary view
  summaryCol1: { width: "25%" }, // EVENT/JOB
  summaryCol1Long: { width: "35%" }, // EVENT/JOB
  summaryCol2: { width: "15%" }, // DATE
  summaryCol3: { width: "20%" }, // POSITION
  summaryCol3Long: { width: "25%" }, // POSITION
  summaryCol4: { width: "12%" }, // QTY STAFF
  summaryCol5: { width: "13%" }, // RATE
  summaryCol6: { width: "15%" }, // AMOUNT

  totalsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 16,
    borderTop: "1px solid #e0e0e0",
  },
  totalsBox: {
    width: 200,
  },
  notesBox: {
    width: 200,
  },
  totalsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  notesRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    marginBottom: 8,
  },
  totalsDivider: {
    borderBottom: "1px solid #e0e0e0",
    marginVertical: 8,
  },
  totalsFinal: {
    fontSize: 14,
    fontWeight: "bold",
  },
  notesFinal: {
    fontSize: 14,
  },
});

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const InvoiceEditorPreviewPDF = ({
  invoiceNumber,
  invoiceInformation,
  from,
  to,
  invoiceViewMode,
  detailRows,
  summaryRows,
  subTotal,
  total,
  tax,
  discount,
  getTotalAmount,
  isShiftJob,
  isVoid,
}) => {
  const renderDetailTable = () => (
    <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        {!isShiftJob && <Text style={[styles.tableHeaderCell, styles.detailCol1]}>DATE</Text>}
        <Text
          style={[styles.tableHeaderCell, isShiftJob ? styles.detailCol2Long : styles.detailCol2]}
        >
          {isShiftJob ? "JOB" : "EVENT"}
        </Text>
        <Text
          style={[styles.tableHeaderCell, isShiftJob ? styles.detailCol3Long : styles.detailCol3]}
        >
          EMPLOYEE NAME
        </Text>
        <Text style={[styles.tableHeaderCell, styles.detailCol4]}>POSITION</Text>
        <Text style={[styles.tableHeaderCell, styles.detailCol5]}>PAYCODE</Text>
        <Text style={[styles.tableHeaderCell, styles.detailCol6]}>REG HOURS</Text>
        <Text style={[styles.tableHeaderCell, styles.detailCol7]}>OT HOURS</Text>
        <Text style={[styles.tableHeaderCell, styles.detailCol8]}>RATE</Text>
        <Text style={[styles.tableHeaderCell, styles.detailCol9]}>AMOUNT</Text>
      </View>

      {/* Table Body */}
      {detailRows.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <View key={index} style={styles.tableRow}>
          {!isShiftJob && <Text style={[styles.tableCell, styles.detailCol1]}>{item.date}</Text>}
          <Text style={[styles.tableCell, isShiftJob ? styles.detailCol2Long : styles.detailCol2]}>
            {isShiftJob ? item.jobName : item.eventName}
          </Text>
          <Text
            style={[styles.tableCellMedium, isShiftJob ? styles.detailCol3Long : styles.detailCol3]}
          >
            {item.firstName} {item.lastName}
          </Text>
          <Text style={[styles.tableCell, styles.detailCol4]}>
            {item.positionName || (isShiftJob ? "Job Staff" : "Event Staff")}
          </Text>
          <Text style={[styles.tableCell, styles.detailCol5]}>{item.earningId}</Text>
          <Text style={[styles.tableCell, styles.detailCol6]}>{item.totalHours}</Text>
          <Text style={[styles.tableCell, styles.detailCol7]}>
            {item.totalOvertimeHours || "0"}
          </Text>
          <Text style={[styles.tableCell, styles.detailCol8]}>
            {moneyFormatter.format(parseFloat(item.billRate?.toString() || "0"))}
          </Text>
          <Text style={[styles.tableCell, styles.detailCol9]}>{getTotalAmount(item)}</Text>
        </View>
      ))}
    </View>
  );

  const renderSummaryTable = () => (
    <View style={styles.table}>
      {/* Table Header */}
      <View style={styles.tableHeader}>
        <Text
          style={[styles.tableHeaderCell, isShiftJob ? styles.summaryCol1Long : styles.summaryCol1]}
        >
          {isShiftJob ? "JOB" : "EVENT"}
        </Text>
        {!isShiftJob && <Text style={[styles.tableHeaderCell, styles.summaryCol2]}>DATE</Text>}
        <Text
          style={[styles.tableHeaderCell, isShiftJob ? styles.summaryCol3Long : styles.summaryCol3]}
        >
          POSITION
        </Text>
        <Text style={[styles.tableHeaderCell, styles.summaryCol4]}>QTY STAFF</Text>
        <Text style={[styles.tableHeaderCell, styles.summaryCol5]}>RATE</Text>
        <Text style={[styles.tableHeaderCell, styles.summaryCol6]}>AMOUNT</Text>
      </View>

      {/* Table Body */}
      {summaryRows.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <View key={index} style={styles.tableRow}>
          <Text
            style={[styles.tableCell, isShiftJob ? styles.summaryCol1Long : styles.summaryCol1]}
          >
            {isShiftJob ? item.jobName : item.eventName}
          </Text>
          {!isShiftJob && <Text style={[styles.tableCell, styles.summaryCol2]}>{item.date}</Text>}
          <Text
            style={[styles.tableCell, isShiftJob ? styles.summaryCol3Long : styles.summaryCol3]}
          >
            {item.positionName || (isShiftJob ? "Job Staff" : "Event Staff")}
          </Text>
          <Text style={[styles.tableCell, styles.summaryCol4]}>{item.totalEmployees}</Text>
          <Text style={[styles.tableCell, styles.summaryCol5]}>
            {moneyFormatter.format(parseFloat(item.billRate?.toString() || "0"))}
          </Text>
          <Text style={[styles.tableCell, styles.summaryCol6]}>{getTotalAmount(item)}</Text>
        </View>
      ))}
    </View>
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Invoice Header */}
        <View style={styles.invoiceHeader}>
          <View>
            <Text>
              <Text style={[styles.invoiceTitle]}>INVOICE</Text>
              {isVoid && <Text style={[styles.invoiceTitle, styles.voidLabel]}> (VOID)</Text>}
            </Text>

            <Text style={styles.invoiceInfo}>
              <Text style={{ fontWeight: "bold" }}>Invoice Number:</Text> {invoiceNumber}
            </Text>
            <Text style={styles.invoiceInfo}>
              <Text style={{ fontWeight: "bold" }}>Purchase Order:</Text>{" "}
              {invoiceInformation.purchaseOrder || "-"}
            </Text>
            <Text style={styles.invoiceInfo}>
              <Text style={{ fontWeight: "bold" }}>Invoice Date:</Text> {invoiceInformation.date}
            </Text>
            <Text style={styles.invoiceInfo}>
              <Text style={{ fontWeight: "bold" }}>Due Date:</Text> {invoiceInformation.dueDate}
            </Text>
          </View>

          <View style={styles.companyInfo}>
            <Text style={styles.sectionTitle}>From:</Text>
            <Text style={[styles.invoiceInfo, { fontWeight: "bold" }]}>{from.name}</Text>
            <Text style={styles.invoiceInfo}>{from.address}</Text>
            <Text style={styles.invoiceInfo}>
              {from.city}, {from.state} {from.zip}
            </Text>
            <Text style={styles.invoiceInfo}>
              <Text style={{ fontWeight: "bold" }}>Attn:</Text> {from.attn || "-"}
            </Text>
          </View>
        </View>

        {/* Customer Information */}
        <View style={styles.customerInfo}>
          <Text style={styles.sectionTitle}>Bill To:</Text>
          <Text style={styles.customerName}>{to.name}</Text>
          <Text style={styles.invoiceInfo}>{to.address}</Text>
          <Text style={styles.invoiceInfo}>
            {to.city}, {to.state} {to.zip}
          </Text>
          <Text style={styles.invoiceInfo}>
            <Text style={{ fontWeight: "bold" }}>Attn:</Text> {to.attn || "-"}
          </Text>
        </View>

        {/* Invoice Items Table */}
        {invoiceViewMode === "details" ? renderDetailTable() : renderSummaryTable()}

        {/* Invoice Totals */}
        <View style={styles.totalsContainer}>
          <View style={styles.notesBox}>
            <View style={styles.totalsRow}>
              <Text style={styles.notesFinal}>
                <Text style={{ fontWeight: "bold" }}>Notes:</Text>
                {` ${from?.notes || "-"}`}
              </Text>
            </View>
          </View>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text style={styles.totalsFinal}>Total:</Text>
              <Text style={styles.totalsFinal}>{moneyFormatter.format(total)}</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

// Setting default values for the props
InvoiceEditorPreviewPDF.defaultProps = {};

// Typechecking props
InvoiceEditorPreviewPDF.propTypes = {};

export default InvoiceEditorPreviewPDF;
